import { AnyAction } from '@reduxjs/toolkit';
import { isResponseWithData } from 'lib-shared-auth';
import type { UpdateConversationQueueTransferApiResponse } from 'lib-shared-types-api';
import {
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { AppState } from 'bootstrap.store';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { notificationActions } from 'components/notifications/actions/notification';
import { presenceActions } from 'components/presence/actions/presenceActions';
import { twilioActions } from 'middleware/twilio/actions/twilioActions';
import { WebSocketConversationTransferStatusInboundMessage } from 'middleware/webSocket';

import {
  ConversationTransferQueueRequestRejected,
  ConversationTransferRequest,
} from './types/appConversationTransfer.types';
import { getConversationTransferRequestNotificationId } from './utils/conversationTransfer/conversationTransfer';

const { notificationAdd, notificationRemoveByType } = notificationActions;
const { twilioConnectCall } = twilioActions;
const { conversationClose } = conversationActions;
const { presenceGetSucceeded } = presenceActions;

export const appSagas = [
  takeLatest(presenceGetSucceeded.toString(), shouldReconnectToPhone()),
  takeEvery(
    appActions.conversationTransferRequest.pending,
    conversationTransfer,
  ),
];

/**
 * We check that we don't have an active phone call. If we do we show a
 * notification asking the agent to reconnect.
 *
 * TODO: [TEAMS-7333] Refactor and move this to where we fetch the agent
 * presence on app init. The logic should then also be changed to only test for
 * the agent presence and not the agents presence.
 */
function shouldReconnectToPhone() {
  // We suppress this until the action created through rtk.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function* (action: any) {
    try {
      const isMicrophoneBlocked = yield* select(
        (state: AppState) => state.telephony.isMicrophoneBlocked,
      );

      // See if any of the conversations are of the type pstnphone or callback,
      // status is open, and not just acw. We also check that the agents
      // microphone not is blocked.
      if (!isMicrophoneBlocked) {
        for (const conversationRequester of action.payload.data.presence
          .presence_status.load_descriptions) {
          if (conversationRequester.conversation !== null) {
            if (
              conversationRequester.conversation.status === 'open' &&
              conversationRequester.load_type !== 'acw'
            ) {
              if (
                (conversationRequester.conversation.initial_channel ===
                  'pstnphone' &&
                  conversationRequester.load_type !== 'nonlive') ||
                (conversationRequester.conversation.initial_channel ===
                  'callback' &&
                  conversationRequester.conversation.is_callback_consumed ===
                    false)
              ) {
                /**
                 * Show notification telling that there still is an active phone
                 * conversation. You still have an active conversation, asking
                 * would you like to reconnect?
                 *
                 * Because we are inside a redux-saga and below we need to fire
                 * another another saga, it is required that "put" effect also
                 * needs to be a generator function.
                 */
                const acceptObj = {
                  text: 'Resume',

                  action: function* () {
                    yield* put(
                      notificationRemoveByType({
                        type: 'activePhoneCall',
                      }),
                    );
                    // re-subscribe to the Twilio conference.
                    yield* put(
                      twilioConnectCall({
                        csid: conversationRequester.conversation.id,
                      }),
                    );
                  },
                };

                const rejectObj = {
                  text: 'Hang up',
                  action: function* () {
                    yield* put(
                      notificationRemoveByType({
                        type: 'activePhoneCall',
                      }),
                    );
                    yield* put(
                      conversationClose({
                        csid: conversationRequester.conversation.id,
                      }),
                    );
                  },
                };

                const notificationOptions = {
                  type: 'activePhoneCall',
                  text: `You have an active phone call, would you like to resume?`,
                  title: 'Active phone call',
                  showDesktop: true,
                  icon: 'i-info',
                  leftAction: acceptObj,
                  rightAction: rejectObj,
                };
                yield put(notificationAdd(notificationOptions));

                break;
              }
            }
          }
        }
      }
    } catch (exception) {
      console.warn('shouldReconnectToPhone', exception);
    }
  };
}

/** Commences the conversation transfer. */
function* conversationTransfer({
  payload,
}: ReturnType<typeof appActions.conversationTransferRequest.pending>) {
  const { csid, type } = payload.request;

  yield* put(
    notificationActions.notificationAdd({
      id: getConversationTransferRequestNotificationId(type, csid),
      title: `Transfer request`,
      text: `Conversation transfer request created for conversation #${csid}`,
      status: 'success',
      icon: 'info',
      time: 5000,
    }),
  );

  // We need to subscribe to the conversation as we would otherwise not
  // receive the WebSocket `conversation:status_changed` message that in turn
  // removes the transfer request from the store.
  yield* put(
    websocketActions.request({
      type: 'connection:subscribe',
      params: {
        type: 'conversation',
        csid,
      },
    }),
  );

  // Initiate the network call based on the transfer type.
  const transferApiPromise = yield* call(transferApiRequest, payload);
  const transferApiResponse: UpdateConversationQueueTransferApiResponse =
    yield transferApiPromise.unwrap();
  transferApiPromise.unsubscribe();

  if (isResponseWithData(transferApiResponse)) {
    yield* call(handleConversationTransferApiFulfilled, csid);
  } else {
    yield* call(handleConversationTransferApiRejected, csid, type);
  }

  // Removes the transfer request started notification.
  yield* put(
    notificationActions.notificationRemoveById({
      id: getConversationTransferRequestNotificationId(type, csid),
    }),
  );

  // Removes the legacy transfer fulfilled notifications in order to not have
  // duplicate notifications. Once the transfers happen through the new logic,
  // logic can be removed.
  yield* put(
    notificationActions.notificationRemoveByType({
      type: `transfer_status_${csid}`,
    }),
  );

  yield* put(
    websocketActions.request({
      type: 'connection:unsubscribe',
      params: {
        type: 'conversation',
        csid,
      },
    }),
  );
}

function* handleConversationTransferApiRejected(
  csid: number,
  type: ConversationTransferQueueRequestRejected['error']['type'],
) {
  yield* put(
    appActions.conversationTransferRequest.rejected({
      error: { csid, type },
    }),
  );
  yield* put(
    notificationActions.notificationAdd({
      title: 'Could not transfer the conversation',
      text: `An issue occurred while trying transfer conversation ${csid}.`,
      status: 'danger',
      icon: 'exclamation',
    }),
  );
}

function* handleConversationTransferApiFulfilled(csid: number) {
  const action = yield* take<
    ReturnType<typeof appActions.conversationTransferRequest.fulfilled>
  >(
    (action: AnyAction) =>
      action.type === appActions.conversationTransferRequest.fulfilled.type &&
      action.payload.response.params.csid === csid,
  );

  yield* call(
    dispatchTransferRequestFulfilledNotification,
    action.payload.response.params,
  );
}

function* dispatchTransferRequestFulfilledNotification(
  params: WebSocketConversationTransferStatusInboundMessage['params'],
) {
  // NOTE: Requires transformation in WebSocket response; raw response returns `internalqueue`.
  if (params.type === 'internal_queue') {
    yield* put(
      notificationActions.notificationAdd({
        title: 'Conversation transferred',
        text: `#${params.csid} was successfully transferred`,
        status: 'success',
        icon: 'info',
      }),
    );
  }
}

function* transferApiRequest(payload: ConversationTransferRequest) {
  switch (payload.request.type) {
    // NOTE: Requires transformation in WebSocket response; raw response returns
    // `internalqueue`.
    case 'internal_queue':
    default: {
      return yield* put(
        conversationsApi.endpoints.updateConversationQueueTransfer.initiate({
          queueId: payload.request.queue.id,
          csid: payload.request.csid,
        }),
      );
    }
  }
}
